import { Fragment, type MouseEvent, type ReactNode } from "react";
import { ArrowLeft } from "lucide-react";

import { AppLink } from "@/components/app-link";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export interface Crumb {
  label: string;
  href?: string;
  params?: Record<string, string>;
  onClick?: (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
}

export interface PageHeaderProps {
  /** Names the entity on this page. Rendered as the page's h1. */
  title: string;
  /**
   * Ancestor links, nearest-last, rendered inline before the title as
   * "← SpecPilot / Job name / Title". Omit on top-level pages; never include the current page.
   */
  trail?: Crumb[];
  /** Inline status/badges next to the title. */
  meta?: ReactNode;
  /** `size="sm"` buttons. The strip scrolls horizontally on narrow screens. */
  actions?: ReactNode;
  /** Discouraged — a short muted line under the row for first-run/empty explanations only. */
  description?: ReactNode;
  className?: string;
}

/**
 * Sticky, full-bleed header row. Cancels the page padding (`p-6 lg:p-8` on the AppShell
 * `page-content`), so render it as the first child of the page; the
 * parent's `space-y-4` / `gap-4` spaces the content below it.
 */
const HEADER_FRAME_CLASSES =
  "sticky top-0 z-30 -mx-6 -mt-6 border-b border-border bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:-mx-8 lg:-mt-8 lg:px-8";

function TrailCrumb({ crumb }: { crumb: Crumb }) {
  const classes = "max-w-[18ch] truncate";
  if (crumb.href) {
    return (
      <AppLink
        to={crumb.href}
        params={crumb.params}
        title={crumb.label}
        onClick={crumb.onClick}
        className={cn(
          classes,
          // text-sm/tracking-normal: AppLink renders a ghost Button in workspace mode.
          "rounded-sm text-sm tracking-normal text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        )}
      >
        {crumb.label}
      </AppLink>
    );
  }
  if (crumb.onClick) {
    return (
      <button
        type="button"
        title={crumb.label}
        onClick={crumb.onClick}
        className={cn(
          classes,
          "rounded-sm text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        )}
      >
        {crumb.label}
      </button>
    );
  }
  return (
    <span className={classes} title={crumb.label}>
      {crumb.label}
    </span>
  );
}

export function PageHeader({
  title,
  trail,
  meta,
  actions,
  description,
  className,
}: PageHeaderProps) {
  const hasTrail = trail !== undefined && trail.length > 0;
  return (
    <header data-slot="page-header" className={cn(HEADER_FRAME_CLASSES, className)}>
      <div className="flex min-h-12 flex-wrap items-center gap-x-4 gap-y-1 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {hasTrail ? (
            <nav aria-label="Breadcrumb" className="min-w-0 shrink">
              <ol className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
                <li aria-hidden="true" className="flex shrink-0">
                  <ArrowLeft className="size-4" />
                </li>
                {trail.map((crumb, index) => (
                  <Fragment key={`${crumb.href ?? ""}:${crumb.label}:${index}`}>
                    <li className="flex min-w-0">
                      <TrailCrumb crumb={crumb} />
                    </li>
                    <li aria-hidden="true" className="shrink-0 text-muted-foreground/60">
                      /
                    </li>
                  </Fragment>
                ))}
              </ol>
            </nav>
          ) : null}
          <h1 className="min-w-0 truncate text-lg font-semibold text-foreground" title={title}>
            {title}
          </h1>
          {meta ? <div className="flex shrink-0 items-center gap-2">{meta}</div> : null}
        </div>
        {actions ? (
          // py-1/-my-1 leaves room for focus rings inside the scroll container.
          <div className="ms-auto -my-1 flex max-w-full items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden py-1">
            {actions}
          </div>
        ) : null}
      </div>
      {description ? (
        <div className="-mt-1 pb-2 text-sm text-muted-foreground">{description}</div>
      ) : null}
    </header>
  );
}

/** Placeholder header for loading branches (same frame, no "Loading..." crumbs). */
export function PageHeaderSkeleton({
  withTrail = false,
  className,
}: {
  withTrail?: boolean;
  className?: string;
}) {
  return (
    <div
      data-slot="page-header"
      aria-hidden="true"
      className={cn(HEADER_FRAME_CLASSES, className)}
    >
      <div className="flex min-h-12 items-center gap-2 py-2">
        {withTrail ? <Skeleton className="h-4 w-28" /> : null}
        <Skeleton className="h-5 w-48" />
      </div>
    </div>
  );
}

/*
 * Usage Example:
 *
 * import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
 * import { Button } from "@/components/ui/button";
 *
 * function SubmittalPage({ job }: { job?: { _id: string; name: string } }) {
 *   if (!job) return <PageHeaderSkeleton withTrail />;
 *   return (
 *     <div className="space-y-4">
 *       <PageHeader
 *         title="Submittal"
 *         trail={[
 *           { label: "SpecPilot", href: "/specPilot" },
 *           { label: job.name, href: "/specPilot/$jobId", params: { jobId: job._id } },
 *         ]}
 *         meta={<JobStatusBadge status="completed" />}
 *         actions={<Button size="sm">Export</Button>}
 *       />
 *     </div>
 *   );
 * }
 */
